import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area,
} from "recharts";
import { Inbox, TrendingUp, Trophy, Loader2, Users, Eye } from "lucide-react";
import { api } from "@/lib/api";

const tooltipStyle = {
  contentStyle: { background: "#1A1A26", border: "1px solid #282836", borderRadius: 12, color: "#F4F4F8" },
  labelStyle: { color: "#9E9EB3" },
  cursor: { fill: "rgba(255,85,0,0.08)" },
};

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-[#12121A] border border-[#282836] rounded-2xl p-6">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-[#FF5500]/10 border border-[#FF5500]/30 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#FF5500]" />
      </div>
      <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#63637A]">{label}</span>
    </div>
    <p className="font-display text-3xl font-bold">{value}</p>
    {sub && <p className="text-sm text-[#9E9EB3] mt-1 truncate">{sub}</p>}
  </div>
);

const AppBarChart = ({ title, data, testid }) => (
  <div data-testid={testid} className="bg-[#12121A] border border-[#282836] rounded-2xl p-6">
    <h3 className="font-display font-semibold mb-6">{title}</h3>
    {data.length === 0 ? (
      <p className="text-[#63637A] text-sm py-16 text-center">No data yet</p>
    ) : (
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
          <CartesianGrid stroke="#282836" horizontal={false} />
          <XAxis type="number" allowDecimals={false} stroke="#63637A" fontSize={11} />
          <YAxis type="category" dataKey="name" width={130} stroke="#9E9EB3" fontSize={11} tickLine={false} />
          <Tooltip {...tooltipStyle} />
          <Bar dataKey="count" fill="#FF5500" radius={[0, 8, 8, 0]} maxBarSize={26} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

export const AnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [traffic, setTraffic] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/inquiries"),
      api.get("/analytics/traffic"),
    ])
      .then(([inq, trf]) => { setData(inq.data); setTraffic(trf.data); })
      .catch(() => {
        setData({ total: 0, by_app: [], by_day: [] });
        setTraffic({ unique_visitors: 0, unique_visitors_30d: 0, total_app_views: 0, app_views: [] });
      });
  }, []);

  if (!data || !traffic) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5500]" />
      </div>
    );
  }

  const weekTotal = data.by_day.slice(-7).reduce((s, d) => s + d.count, 0);
  const topApp = data.by_app[0];
  const topViewed = traffic.app_views[0];
  const dayData = data.by_day.map((d) => ({ ...d, label: d.date.slice(5) }));

  return (
    <div data-testid="analytics-panel">
      <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#63637A] mb-4">Visitors</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        <StatCard icon={Users} label="Unique visitors (all time)" value={traffic.unique_visitors} testid="stat-visitors" />
        <StatCard icon={Users} label="Unique visitors (30 days)" value={traffic.unique_visitors_30d} testid="stat-visitors-30d" />
        <StatCard icon={Eye} label="App popups opened" value={traffic.total_app_views} sub={topViewed ? `Most viewed: ${topViewed.name}` : "—"} testid="stat-app-views" />
      </div>

      <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#63637A] mb-4">Inquiries</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        <StatCard icon={Inbox} label="Total inquiries" value={data.total} testid="stat-total" />
        <StatCard icon={TrendingUp} label="Last 7 days" value={weekTotal} testid="stat-week" />
        <StatCard icon={Trophy} label="Most requested app" value={topApp ? topApp.count : 0} sub={topApp ? topApp.name : "—"} testid="stat-top-app" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <AppBarChart title="Most viewed apps (popups opened)" data={traffic.app_views} testid="chart-app-views" />
        <AppBarChart title="Inquiries per app" data={data.by_app} testid="chart-by-app" />
      </div>

      <div data-testid="chart-by-day" className="bg-[#12121A] border border-[#282836] rounded-2xl p-6">
        <h3 className="font-display font-semibold mb-6">Inquiries — last 30 days</h3>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={dayData} margin={{ left: -16, right: 8 }}>
            <defs>
              <linearGradient id="inquiryFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF5500" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#FF5500" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#282836" vertical={false} />
            <XAxis dataKey="label" stroke="#63637A" fontSize={10} tickLine={false} interval={4} />
            <YAxis allowDecimals={false} stroke="#63637A" fontSize={11} tickLine={false} />
            <Tooltip {...tooltipStyle} />
            <Area type="monotone" dataKey="count" stroke="#FF5500" strokeWidth={2} fill="url(#inquiryFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
